import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ServicesList from "./components/ServicesList";
import AdditionalServices from "./components/AdditionalServices";
import ServicesContact from "./components/ServicesContact";
import GutterBenefits from "./components/GutterBenefits";
import ServicesWhyChooseUs from "./components/ServicesWhyChooseUs";
import ServicesFAQ from "./components/ServicesFAQ";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://mightycleaningservices.co.uk";

const servicesWebPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${siteUrl}/services#webpage`,
  "url": `${siteUrl}/services`,
  "name": "Window Cleaning & Gutter Clearance Services Northfleet Kent | Mighty Cleaning Services Ltd",
  "description":
    "Professional window cleaning, gutter clearance, fascia & soffit cleaning, pressure washing and solar panel cleaning in Northfleet, Gravesend and Kent. Residential & commercial. Get a free quote today.",
  "inLanguage": "en-GB",
  "isPartOf": {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    "name": "Mighty Cleaning Services Ltd",
    "url": siteUrl,
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteUrl}/` },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${siteUrl}/services` },
    ],
  },
};

const servicesFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How often should I have my gutters cleaned?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For most residential properties in Northfleet and Gravesend, we recommend having your gutters cleared at least twice a year — typically in late autumn after leaves have fallen, and again in spring to remove moss and debris that builds up over winter. Properties surrounded by trees may benefit from quarterly gutter clearance.",
      },
    },
    {
      "@type": "Question",
      "name": "What technology do you use to clean gutters?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "At Mighty Cleaning Services Ltd, we use the latest SkyVac gutter clearance technology — a high-powered industrial vacuum system with carbon fibre poles and a camera head that allows us to clear and inspect gutters from ground level in many cases.",
      },
    },
    {
      "@type": "Question",
      "name": "Do you offer gutter clearance services for both residential and commercial properties?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes — Mighty Cleaning Services Ltd provides professional gutter clearance for both residential and commercial properties across Northfleet, Gravesend, and the wider Kent area. We offer flexible one-off clears and regular maintenance plans.",
      },
    },
    {
      "@type": "Question",
      "name": "What are the signs that my gutters need clearing?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The most obvious signs of blocked gutters include water overflowing over the edge during rain, damp patches or staining on external walls, visible plant growth or moss inside the guttering, sagging gutters, and water pooling near your foundations.",
      },
    },
    {
      "@type": "Question",
      "name": "How long does a gutter clearance take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A standard residential gutter clearance typically takes between 30 minutes and 1.5 hours depending on the size of the property, the amount of debris present, and the accessibility of the guttering system.",
      },
    },
  ],
};

const servicesOfferJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Window Cleaning and Exterior Cleaning",
  "name": "Professional Window Cleaning & Gutter Clearance",
  "description":
    "Window cleaning, gutter clearance, fascia & soffit cleaning, conservatory roof cleaning, pressure washing and solar panel cleaning in Northfleet, Gravesend and Kent.",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Mighty Cleaning Services Ltd",
    "telephone": "02084322244",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Northfleet",
      "addressRegion": "Kent",
      "postalCode": "DA11 9HJ",
      "addressCountry": "GB",
    },
  },
  "areaServed": [
    { "@type": "City", "name": "Northfleet" },
    { "@type": "City", "name": "Gravesend" },
    { "@type": "City", "name": "Dartford" },
    { "@type": "AdministrativeArea", "name": "Kent" },
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Cleaning Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Window Cleaning" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Gutter Clearance" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Fascia & Soffit Cleaning" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pressure Washing" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Solar Panel Cleaning" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Conservatory Roof Cleaning" } },
    ],
  },
};

export default function ServicesPage() {
  return (
    <div className="font-body">
      <SEOHead
        title="Window Cleaning & Gutter Clearance Services Northfleet Kent | Mighty Cleaning Services Ltd"
        description="Professional window cleaning, gutter clearance, fascia & soffit cleaning, conservatory roof cleaning, pressure washing and solar panel cleaning in Northfleet, Gravesend and Kent. Residential & commercial. Get a free quote today."
        keywords="window cleaning services Kent, gutter clearance Northfleet, fascia soffit cleaning Gravesend, pressure washing Kent, solar panel cleaning Northfleet, conservatory roof cleaning Kent"
        canonical={`${siteUrl}/services`}
      />
      <JsonLd id="services-webpage" data={servicesWebPageJsonLd} />
      <JsonLd id="services-offer" data={servicesOfferJsonLd} />
      <JsonLd id="services-faq" data={servicesFaqJsonLd} />
      <Navbar />
      <main>
        {/* Page Hero */}
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/60ffb1d5a2c1e31f0d5ea277ec37fff4.jpg"
              alt="Professional window cleaning and gutter clearance services Northfleet Gravesend"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F2040]/90 via-[#0F2040]/75 to-[#0F2040]/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
            <span className="text-[#60AEFF] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
              Our Services
            </span>
            <h1
              className="font-heading font-800 text-white leading-tight mb-5 max-w-2xl"
              style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}
            >
              Window Cleaning &amp; Gutter Clearance Services in Northfleet, Gravesend and Kent.
            </h1>
            <p className="text-blue-100 font-body text-lg leading-relaxed max-w-xl mb-8">
              From homes to high-rises, from sparkling windows to clear gutters — we deliver spotless results for every property type across Northfleet, Gravesend and Kent.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#quote-form"
                className="flex items-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                Get a Free Quote
              </a>
              <a
                href="tel:02084322244"
                className="flex items-center gap-2 border border-white/40 text-white hover:bg-white/10 font-heading font-semibold text-sm px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i>
                0208 43 222 44
              </a>
            </div>
          </div>
        </section>

        <ServicesList />
        <AdditionalServices />
        <GutterBenefits />
        <ServicesWhyChooseUs />
        <ServicesFAQ />
        <ServicesContact />
      </main>
      <Footer />
    </div>
  );
}
